import { ReactNode, useState } from "react";

import {
  Accordion,
  AccordionButton,
  Card as BootstrapCard,
  Collapse,
} from "react-bootstrap";
import styled from "styled-components";

export const DEFAULT_UNIT_WIDTH = 400;
export const DEFAULT_UNIT_HEIGHT = 100;
export const DEFAULT_GRID_GAP = 20;
export const DEFAULT_COL_SPAN = 1;
export const DEFAULT_ROW_SPAN = 4;

const CardGridContainer = styled.div`
  container-name: aa-card-grid-container;
  container-type: inline-size;
`;

const StyledCardGrid = styled.div<{
  $unitHeight: number;
  $unitWidth: number;
  $maxColumns: number;
  $minWidth?: string;
}>`
  display: grid;
  grid-auto-flow: row dense;
  min-width: ${({ $minWidth }) => $minWidth};
  --aa-card-grid-column-count: 1;
  --aa-card-grid-column-width: 100%;

  ${({ $maxColumns, $unitWidth }) => {
    const col2 = Math.min($maxColumns, 2);
    const col3 = Math.min($maxColumns, 3);
    const col4 = Math.min($maxColumns, 4);
    const w2 = `calc(50% - ${(1 / 2) * DEFAULT_GRID_GAP}px)`;
    const w3 = `calc(33% - ${(2 / 3) * DEFAULT_GRID_GAP}px)`;
    const w4 = `calc(25% - ${(3 / 4) * DEFAULT_GRID_GAP}px)`;

    // @media queries provide baseline responsive behavior that survives
    // browser extensions which rewrite stylesheets and drop @container rules.
    // @container queries refine the layout based on actual container width.
    return `
    ${
      $maxColumns >= 2
        ? `@media (min-width: ${$unitWidth * 1}px) {
            & {
              --aa-card-grid-column-count: ${col2};
              --aa-card-grid-column-width: ${w2};
            }
          }`
        : ``
    }
    ${
      $maxColumns >= 3
        ? `@media (min-width: ${$unitWidth * 2}px) {
            & {
              --aa-card-grid-column-count: ${col3};
              --aa-card-grid-column-width: ${w3};
            }
          }`
        : ``
    }
    ${
      $maxColumns >= 4
        ? `@media (min-width: ${$unitWidth * 3}px) {
            & {
              --aa-card-grid-column-count: ${col4};
              --aa-card-grid-column-width: ${w4};
            }
          }`
        : ``
    }

    ${
      $maxColumns >= 2
        ? `@container aa-card-grid-container (width > ${$unitWidth * 1}px) {
            --aa-card-grid-column-count: ${col2};
            --aa-card-grid-column-width: ${w2};
          }`
        : ``
    }
    ${
      $maxColumns >= 3
        ? `@container aa-card-grid-container (width > ${$unitWidth * 2}px) {
            --aa-card-grid-column-count: ${col3};
            --aa-card-grid-column-width: ${w3};
          }`
        : ``
    }
    ${
      $maxColumns >= 4
        ? `@container aa-card-grid-container (width > ${$unitWidth * 3}px) {
            --aa-card-grid-column-count: ${col4};
            --aa-card-grid-column-width: ${w4};
          }`
        : ``
    }
    `;
  }}

  grid-template-columns: repeat(
    var(--aa-card-grid-column-count),
    var(--aa-card-grid-column-width)
  );
  grid-template-rows: min-content;
  width: 100%;
  gap: ${DEFAULT_GRID_GAP}px;
  margin-bottom: 20px;
`;

const CardContainer = styled.div<{ $colSpan: number; $rowSpan: number }>`
  grid-column: span
    min(${({ $colSpan }) => $colSpan}, var(--aa-card-grid-column-count));
  grid-row: span ${({ $rowSpan }) => $rowSpan};
`;

const StyledBootstrapCard = styled(BootstrapCard)`
  height: 100%;
`;

const StyledAccordionButton = styled(AccordionButton)`
  padding: 0px;
  &:not(:focus) {
    background: none !important;
    box-shadow: none !important;
  }
`;

const CardFlexBox = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  gap: 20px;
`;

const CardSiderFlexContainer = styled.div<{ $showBody: boolean }>`
  display: flex;

  ${({ $showBody }) =>
    $showBody
      ? `
        min-width: 120px;
        max-width: fit-content;
        flex-shrink: 0;
      `
      : `
        flex-grow: 1;
      `};
`;

export const CardSiderFlexContent = styled.div<{ $collapsed?: boolean }>`
  display: flex;

  ${({ $collapsed }) =>
    $collapsed
      ? `
      gap: 40px;
      flex-direction: row;
    `
      : `
      gap: 10px;
      flex-direction: column;
    `}
`;

const CardHeaderFlexContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const CardBodyFlexContainer = styled.div`
  flex-grow: 1;
  max-width: 100%;
  overflow: auto;
`;

const CardToggleFlexContainer = styled.div`
  flex-grow: 0;
`;

export const CardGrid = ({
  unitWidth = DEFAULT_UNIT_WIDTH,
  unitHeight = DEFAULT_UNIT_HEIGHT,
  minWidth,
  maxColumns,
  children,
  className,
}: {
  unitWidth?: number;
  unitHeight?: number;
  minWidth?: string;
  maxColumns?: number;
  children?: ReactNode;
  className?: string;
}) => {
  return (
    <CardGridContainer className={className} style={{ minWidth: minWidth }}>
      <StyledCardGrid
        $unitWidth={unitWidth}
        $unitHeight={unitHeight}
        $maxColumns={maxColumns || 4}
      >
        {children}
      </StyledCardGrid>
    </CardGridContainer>
  );
};

export const Card = ({
  id,
  title,
  children,
  colSpan = DEFAULT_COL_SPAN,
  rowSpan = DEFAULT_ROW_SPAN,
  Sider,
  Header,
  collapsible = false,
  className,
}: {
  id?: string;
  title?: ReactNode;
  children?: ReactNode;
  colSpan?: number;
  rowSpan?: number;
  Sider?: React.FC<{ collapsed: boolean }>;
  Header?: React.FC<{ collapsed: boolean }>;
  collapsible?: boolean;
  // Applied to the card element itself, so styled(Card) can restyle it.
  className?: string;
}) => {
  const [showBody, setShowBody] = useState<boolean>(true);

  return (
    <CardContainer
      key={id}
      $colSpan={colSpan}
      $rowSpan={showBody ? rowSpan : 1}
    >
      <StyledBootstrapCard className={className}>
        <BootstrapCard.Body>
          <Accordion>
            {title && <BootstrapCard.Title>{title}</BootstrapCard.Title>}
            <CardFlexBox>
              {Sider && (
                <CardSiderFlexContainer $showBody={showBody}>
                  <Sider collapsed={!showBody} />
                </CardSiderFlexContainer>
              )}
              <CardBodyFlexContainer>
                {Header && (
                  <CardHeaderFlexContainer>
                    <div className="flex-grow-1">
                      <Header collapsed={!showBody} />
                    </div>
                    {collapsible && (
                      <CardToggleFlexContainer>
                        <StyledAccordionButton
                          onClick={() => setShowBody(!showBody)}
                        />
                      </CardToggleFlexContainer>
                    )}
                  </CardHeaderFlexContainer>
                )}
                <Collapse in={showBody}>
                  <div>{children}</div>
                </Collapse>
              </CardBodyFlexContainer>
              {collapsible && !Header && (
                <CardToggleFlexContainer>
                  <StyledAccordionButton
                    onClick={() => setShowBody(!showBody)}
                  />
                </CardToggleFlexContainer>
              )}
            </CardFlexBox>
          </Accordion>
        </BootstrapCard.Body>
      </StyledBootstrapCard>
    </CardContainer>
  );
};
